import { EqualityCheck, OnChangeRef } from "@/utils/hooks/use-stable-object";
import { useMemo, useRef } from "react";

export type { EqualityCheck };

export function useMemodArray<T>(...array: (T[] | undefined)[]): T[] {
  // Funky special case where we memoize on all elements of the array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo((): T[] => array.filter((x): x is T[] => Array.isArray(x)).flat(), array);
}

export const shallowEquals = <T>(arrA: readonly T[], arrB: readonly T[]): boolean => {
  if (arrA === arrB) {
    return true;
  }

  if (arrA.length !== arrB.length) {
    return false;
  }

  return arrA.every((elementA, index) => arrB[index] === elementA);
};

export const useStableArray = <T>(
  array: readonly T[],
  comparator: EqualityCheck<readonly T[]> = shallowEquals,
  onChange?: OnChangeRef<readonly T[]>,
): readonly T[] => {
  const stableArrayRef = useRef<readonly T[]>(array);

  if (!comparator(array, stableArrayRef.current)) {
    onChange?.(stableArrayRef.current, array);
    stableArrayRef.current = array;
  }

  return stableArrayRef.current;
};
